import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../config/service";
import { AxiosPropertyInstance } from "../../common/utils/AxiosInstance";

/**
 * Live property-service prices (Super-Admin editable) from the DB pricing
 * singleton, as an { [serviceId]: amount } map. Used to overlay the static
 * service list so the displayed price matches what checkout actually charges.
 * Resolves to {} on any failure, so the static prices remain the fallback.
 */
async function fetchDbPropertyServicePrices() {
  try {
    const res = await AxiosPropertyInstance.get(
      "/service-pricing/singleton/propertyService"
    );
    const services = Array.isArray(res?.data?.services)
      ? res.data.services
      : [];
    const map = {};
    services.forEach((s) => {
      if (s && s.id && s.price && s.price.amount != null) {
        map[s.id] = s.price.amount;
      }
    });
    return map;
  } catch {
    return {};
  }
}

/**
 * transformResponse for a property-service FORM payload: overlays each
 * checkbox's `price` with the live DB value (Super-Admin editable) from the
 * propertyService pricing singleton, matched by service id (the form's
 * serviceSlug) + checkbox id. Mirrors the backend checkout rule — DB price when
 * present, else the static form price — so the displayed total for checkbox-
 * priced services (property-alert-service, corporate-alert-service, …) matches
 * what checkout actually charges. Leaves the form untouched on any failure, or
 * for any checkbox the DB doesn't price.
 */
async function overlayDbFormCheckboxPrices(response, _meta, arg) {
  try {
    if (!Array.isArray(response?.checkboxes) || !response.checkboxes.length) {
      return response;
    }
    const serviceId = arg?.serviceSlug;
    if (!serviceId) return response;
    const res = await AxiosPropertyInstance.get(
      "/service-pricing/singleton/propertyService"
    );
    const services = Array.isArray(res?.data?.services)
      ? res.data.services
      : [];
    const svc = services.find((s) => s && s.id === serviceId);
    const dbCheckboxes = Array.isArray(svc?.checkboxes) ? svc.checkboxes : [];
    if (!dbCheckboxes.length) return response;
    const priceById = {};
    dbCheckboxes.forEach((cb) => {
      if (cb && cb.id && cb.price != null) priceById[cb.id] = cb.price;
    });
    return {
      ...response,
      checkboxes: response.checkboxes.map((cb) =>
        cb && priceById[cb.id] != null ? { ...cb, price: priceById[cb.id] } : cb
      ),
    };
  } catch {
    return response;
  }
}

/**
 * transformResponse for any `{ services: [...] }` property-service payload:
 * replaces each service's price.amount with the live DB value (matched by id),
 * leaving everything else — and any service the DB doesn't price — untouched.
 * Applied only to queries whose result is actually shown with a price.
 */
async function overlayDbPrices(response) {
  try {
    if (!response?.services?.length) return response;
    const priceById = await fetchDbPropertyServicePrices();
    return {
      ...response,
      services: response.services.map((svc) =>
        svc && svc.price && priceById[svc.id] != null
          ? { ...svc, price: { ...svc.price, amount: priceById[svc.id] } }
          : svc
      ),
    };
  } catch {
    return response;
  }
}

/**
 * transformResponse for index2.json (services page): same DB price overlay as
 * overlayDbPrices, applied to every place a service object appears — the main
 * `services` list, `fastmoving`, and each bundle's `services` — so the prices
 * shown on the services page match the service detail page and checkout.
 */
async function overlayDbPricesV2(response) {
  try {
    if (!response?.services?.length) return response;
    const priceById = await fetchDbPropertyServicePrices();
    const withDbPrice = (svc) =>
      svc && svc.price && priceById[svc.id] != null
        ? { ...svc, price: { ...svc.price, amount: priceById[svc.id] } }
        : svc;
    return {
      ...response,
      services: response.services.map(withDbPrice),
      fastmoving: Array.isArray(response.fastmoving)
        ? response.fastmoving.map(withDbPrice)
        : response.fastmoving,
      bundles: Array.isArray(response.bundles)
        ? response.bundles.map((b) =>
            b && Array.isArray(b.services)
              ? { ...b, services: b.services.map(withDbPrice) }
              : b
          )
        : response.bundles,
    };
  } catch {
    return response;
  }
}

export const servicesService = createApi({
  reducerPath: "servicesService",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    allServies: builder.query({
      query: () => {
        return {
          url: `/api/property/static/all-services/property-services/index.json`,
          method: "GET",
        };
      },
      // Overlay live DB prices onto the static service list (shown in the
      // services grid + FillDetails price). Only price.amount is replaced;
      // labels, form links, bundles still come from the static JSON.
      transformResponse: overlayDbPrices,
    }),

    individualService: builder.query({
      query: (id) => {
        return {
          url: `/api/property/static/all-services/${id}/index.json`,
          method: "GET",
        };
      },
      // ServicePage displays filteredServiceCategory.price.amount from this, so
      // overlay the DB price here too. No-op for non-property categories (their
      // ids aren't in the property-service pricing map).
      transformResponse: overlayDbPrices,
    }),
    // index2.json, superset of index.json adding `fastmoving` and `bundles`.
    individualServiceV2: builder.query({
      query: (id) => {
        return {
          url: `/api/property/static/all-services/${id}/index2.json`,
          method: "GET",
        };
      },
      // The services page now shows prices on its cards, so they must be the
      // live DB prices (same as the detail page), not the static JSON ones.
      transformResponse: overlayDbPricesV2,
    }),
    getSingleService: builder.query({
      query: (id) => {
        return {
          url: `/api/property/static/services/${id}/index.json`,
          method: "GET",
        };
      },
    }),
    individualServiceCategory: builder.query({
      query: ({ category, serviceSlug }) => {
        return {
          url: `/api/property/static/all-services/${category}/${serviceSlug}/index.json`,
          method: "GET",
        };
      },
    }),
    individualServiceCategoryForm: builder.query({
      query: ({ category, serviceSlug }) => {
        return {
          url: `/api/property/static/all-services/${category}/${serviceSlug}/form/index.json`,
          method: "GET",
        };
      },
      // Overlay live DB checkbox prices (Super-Admin editable) onto the form's
      // checkboxes, so the displayed total for checkbox-priced services matches
      // what checkout charges. No-op for forms without priced DB checkboxes.
      transformResponse: overlayDbFormCheckboxPrices,
    }),
  }),
});

export const {
  useAllServiesQuery,
  useIndividualServiceQuery,
  useIndividualServiceV2Query,
  useGetSingleServiceQuery,
  useIndividualServiceCategoryQuery,
  useIndividualServiceCategoryFormQuery,
  useLazyAllServiesQuery
} = servicesService;
